import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import { t } from "../i18n";

function formatBits(value = 0) {
  const units = ["bit/s", "Kbit/s", "Mbit/s", "Gbit/s"];
  let size = Number(value) || 0;
  let unit = units[0];
  for (const next of units) {
    unit = next;
    if (size < 1000 || next === units[units.length - 1]) break;
    size /= 1000;
  }
  if (unit === "bit/s") return `${Math.round(size)} bit/s`;
  if (size >= 100) return `${Math.round(size)} ${unit}`;
  if (size >= 10) return `${size.toFixed(1)} ${unit}`;
  return `${size.toFixed(2)} ${unit}`;
}

function DashboardBadge({ icon, value, tooltip }) {
  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, py: 0.72, px: 1.05, bgcolor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 999, minHeight: 38 }}>
        {icon}
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.1, fontWeight: 500 }}>{value}</Typography>
      </Box>
    </Tooltip>
  );
}

export default function SkeletonActionsBar({ currentLocale = "ru", awgProfiles = [], yourIp = "", analyticsAvailable = false, analyticsDashboardVisible = true, onToggleAnalyticsDashboard }) {
  const [resolvedIp, setResolvedIp] = useState(yourIp || "");

  useEffect(() => {
    setResolvedIp(yourIp || "");
  }, [yourIp]);

  useEffect(() => {
    const value = (yourIp || "").trim();
    const isInvalid = !value || value === "127.0.0.1" || value === "::1" || value.startsWith("10.") || value.startsWith("192.168.") || value.startsWith("172.16.") || value.startsWith("172.17.") || value.startsWith("172.18.") || value.startsWith("172.19.") || value.startsWith("172.2") || value.startsWith("172.30.") || value.startsWith("172.31.");
    if (!isInvalid) return undefined;
    let cancelled = false;
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((payload) => {
        if (!cancelled && payload?.ip) setResolvedIp(payload.ip);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [yourIp]);

  const stats = useMemo(() => {
    const totalRate = awgProfiles.reduce((sum, item) => sum + (Number(item?.totalRateBps) || 0), 0);
    const online = awgProfiles.filter((item) => item?.online).length;
    return {
      totalRate,
      totalProfiles: awgProfiles.length,
      online,
    };
  }, [awgProfiles]);

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, pb: 0, bgcolor: "transparent" }}>
      <Box sx={{ width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 1.15, px: { xs: 1.15, sm: 1.35 }, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 4, boxShadow: "0 8px 24px rgba(17, 24, 39, 0.05)", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
            <DashboardBadge icon={<GroupsRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />} value={String(stats.totalProfiles)} tooltip={t("shell.dashboardProfiles", currentLocale)} />
            <DashboardBadge icon={<WifiRoundedIcon sx={{ fontSize: 17, color: stats.online > 0 ? "success.main" : "text.secondary" }} />} value={String(stats.online)} tooltip={t("shell.dashboardOnline", currentLocale)} />
            <DashboardBadge icon={<SwapVertRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />} value={formatBits(stats.totalRate)} tooltip={t("shell.dashboardTraffic", currentLocale)} />
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {analyticsAvailable ? <Tooltip title={currentLocale === "en" ? (analyticsDashboardVisible ? "Hide analytics" : "Show analytics") : (analyticsDashboardVisible ? "Скрыть аналитику" : "Показать аналитику")} arrow><IconButton aria-label={currentLocale === "en" ? "Toggle analytics dashboard" : "Показать или скрыть аналитику"} onClick={onToggleAnalyticsDashboard} sx={{ width: 38, height: 38, bgcolor: "background.default", border: "1px solid", borderColor: "divider", color: analyticsDashboardVisible ? "primary.main" : "text.secondary", "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" } }}>{analyticsDashboardVisible ? <InsertChartRoundedIcon sx={{ fontSize: 19 }} /> : <InsertChartOutlinedRoundedIcon sx={{ fontSize: 19 }} />}</IconButton></Tooltip> : null}
            <DashboardBadge icon={<LanguageRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />} value={resolvedIp || "-"} tooltip={t("shell.dashboardYourIp", currentLocale)} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
